import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  useBulkEditPointOfInterestsQuery,
  useBulkEditTemplateQuery,
  useBulkUpdatePointOfInterestsMutation,
  PointOfInterestsDocument,
  PoiPriority,
  PoiAccessibilityEnum,
  PoiStatus,
} from '@/__generated__/graphql';
import { FormikErrors } from 'formik';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { abyssColors, primary } from '@/theme/colors';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog/ConfirmationDialog';
import { BulkEditColumnDef, BulkEditFormValues } from './types';
import { GENERAL_FIELD_CONFIGS, BULK_EDIT_MANDATORY_KEYS } from './constants';
import { buildBulkEditColumns, buildInitialValues, useBulkEditForm } from './useBulkEditForm';
import { BulkEditTable } from './BulkEditTable';

type FieldChangeSummary = { label: string; count: number };
type SavePreview = {
  updates: ReturnType<typeof buildUpdates>['updates'];
  changedCount: number;
  fields: FieldChangeSummary[];
};

function buildUpdates(
  values: BulkEditFormValues,
  initialValues: BulkEditFormValues,
  columns: BulkEditColumnDef[]
): {
  updates: Array<{
    pointOfInterestId: string;
    status: PoiStatus | null | undefined;
    dueDate: string | null | undefined;
    priority: PoiPriority | null | undefined;
    accessibility: PoiAccessibilityEnum | null | undefined;
    name: string | undefined;
    templateFieldData: Record<string, string> | undefined;
    lastModifiedFor: string;
  }>;
  changedCount: number;
} {
  const colByKey = new Map(columns.map((col) => [col.key, col]));

  const clearableField = (current: string, initial: string): string | null | undefined =>
    current !== initial ? (current !== '' ? current : null) : undefined;

  const requiredField = (current: string, initial: string): string | undefined =>
    current && current !== initial ? current : undefined;

  const updates = Object.entries(values).map(([poiId, fields]) => {
    const initialPoiValues = initialValues[poiId] ?? {};

    const templateFieldData: Record<string, string> = {};
    Object.entries(fields).forEach(([key, value]) => {
      const col = colByKey.get(key);
      if (col?.isGeneral) return;
      const dataKey = col?.fieldKey;
      if (!dataKey) return;
      const trimmed = value.trim();
      if (trimmed !== (initialPoiValues[key] ?? '').trim()) {
        templateFieldData[dataKey] = trimmed;
      }
    });

    const trim = (v: string) => v.trim();

    return {
      pointOfInterestId: poiId,
      status: clearableField(trim(fields.status), trim(initialPoiValues.status ?? '')) as
        | PoiStatus
        | null
        | undefined,
      dueDate: clearableField(trim(fields.dueDate), trim(initialPoiValues.dueDate ?? '')),
      priority: clearableField(trim(fields.priority), trim(initialPoiValues.priority ?? '')) as
        | PoiPriority
        | null
        | undefined,
      accessibility: clearableField(
        trim(fields.accessibility),
        trim(initialPoiValues.accessibility ?? '')
      ) as PoiAccessibilityEnum | null | undefined,
      name: requiredField(trim(fields.name), trim(initialPoiValues.name ?? '')),
      templateFieldData: Object.keys(templateFieldData).length > 0 ? templateFieldData : undefined,
      lastModifiedFor: 'Bulk Edit POI',
    };
  });

  const changedCount = updates.filter(
    (u) =>
      u.status !== undefined ||
      u.dueDate !== undefined ||
      u.priority !== undefined ||
      u.accessibility !== undefined ||
      u.name !== undefined ||
      u.templateFieldData !== undefined
  ).length;

  return { updates, changedCount };
}

type Props = {
  open: boolean;
  selectedPoiIds: string[];
  templateId: string | undefined;
  onClose: () => void;
  onSaved: () => void;
};

export const BulkEditDrawer = ({ open, selectedPoiIds, templateId, onClose, onSaved }: Props) => {
  const structureId = useGetStructureId();
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const navbarHeight = useNavbarHeight();

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [savePreview, setSavePreview] = useState<SavePreview | null>(null);

  const columnStorageKey = templateId ? `bulk-edit-columns-${templateId}` : null;

  useEffect(() => {
    if (!columnStorageKey) return;
    try {
      const stored = localStorage.getItem(columnStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Always ensure mandatory columns are present
          const mandatorySet = new Set<string>(BULK_EDIT_MANDATORY_KEYS);
          const withMandatory = [
            ...BULK_EDIT_MANDATORY_KEYS,
            ...parsed.filter((k) => !mandatorySet.has(k)),
          ];
          setVisibleColumnKeys(withMandatory);
        }
      }
    } catch {
      // ignore malformed storage
    }
  }, [columnStorageKey]);

  const {
    data: templateData,
    loading: templateLoading,
    error: templateError,
    refetch: refetchTemplate,
  } = useBulkEditTemplateQuery({
    variables: { pointOfInterestTemplateId: templateId },
    skip: !open || !templateId,
    fetchPolicy: 'network-only',
  });

  const {
    data,
    loading: poisLoading,
    error: poisError,
    refetch: refetchPois,
  } = useBulkEditPointOfInterestsQuery({
    variables: {
      input: {
        structureId,
        assemblyTemplateId: selectedAssemblyTemplateId,
        poiIds: selectedPoiIds,
        includeManuallyCreated: true,
      },
      pageInfo: { skip: 0, limit: 1_000, sortBy: 'createdAt', sortDir: 1 },
    },
    skip: !open || selectedPoiIds.length === 0,
    fetchPolicy: 'network-only',
  });

  const loading = templateLoading || poisLoading;
  const queryError = templateError ?? poisError;

  const [bulkUpdatePointOfInterests, { loading: isSaving }] =
    useBulkUpdatePointOfInterestsMutation();

  const pois = useMemo(() => data?.pointOfInterests ?? [], [data]);

  const templateFields = useMemo(
    () => templateData?.pointOfInterestTemplate?.fields ?? [],
    [templateData]
  );

  const allGeneralKeys = useMemo(() => GENERAL_FIELD_CONFIGS.map((cfg) => cfg.key), []);

  const columns = useMemo(
    () => buildBulkEditColumns(templateFields, allGeneralKeys),
    [templateFields, allGeneralKeys]
  );

  const initialValues = useMemo(() => buildInitialValues(pois, columns), [pois, columns]);

  const defaultVisibleColumnKeys = useMemo(
    () => columns.filter((col) => col.isBulkEditable || col.isMandatory).map((col) => col.key),
    [columns]
  );

  const effectiveVisibleColumnKeys = useMemo(
    () => (visibleColumnKeys.length > 0 ? visibleColumnKeys : defaultVisibleColumnKeys),
    [visibleColumnKeys, defaultVisibleColumnKeys]
  );

  const handleSaveRequestRef = useRef<() => void>(() => undefined);

  const stableOnSubmit = useCallback((_values: BulkEditFormValues) => {
    handleSaveRequestRef.current();
    return Promise.resolve();
  }, []);

  const formik = useBulkEditForm({
    pois,
    columns,
    onSubmit: stableOnSubmit,
  });

  const handleSaveRequest = useCallback(() => {
    if (!structureId || !templateId) return;

    const { updates, changedCount } = buildUpdates(formik.values, initialValues, columns);

    if (changedCount === 0) {
      showSnackBar('No changes detected. Edit one or more fields before saving.', 'info');
      return;
    }

    const colByFieldKey = new Map(
      columns.filter((col) => col.fieldKey).map((col) => [col.fieldKey, col])
    );
    const fieldCounts = new Map<string, number>();

    const incrementField = (label: string) => {
      fieldCounts.set(label, (fieldCounts.get(label) ?? 0) + 1);
    };

    for (const update of updates) {
      const hasChange =
        update.status !== undefined ||
        update.dueDate !== undefined ||
        update.priority !== undefined ||
        update.accessibility !== undefined ||
        update.name !== undefined ||
        update.templateFieldData !== undefined;

      if (!hasChange) continue;

      if (update.status !== undefined) incrementField('Status');
      if (update.dueDate !== undefined) incrementField('Due Date');
      if (update.priority !== undefined) incrementField('Priority');
      if (update.accessibility !== undefined) incrementField('Accessibility');
      if (update.name !== undefined) incrementField('POI Name');

      for (const key of Object.keys(update.templateFieldData ?? {})) {
        const label = colByFieldKey.get(key)?.label ?? key;
        incrementField(label);
      }
    }

    const fields: FieldChangeSummary[] = [...fieldCounts.entries()].map(([label, count]) => ({
      label,
      count,
    }));

    setSavePreview({ updates, changedCount, fields });
  }, [formik.values, initialValues, columns, structureId, templateId, showSnackBar]);

  handleSaveRequestRef.current = handleSaveRequest;

  const handleConfirmSave = useCallback(async () => {
    if (!savePreview || !structureId || !templateId) return;

    setSavePreview(null);

    try {
      await bulkUpdatePointOfInterests({
        variables: { input: { structureId, templateId, updates: savePreview.updates } },
        refetchQueries: [PointOfInterestsDocument],
        awaitRefetchQueries: true,
      });
      const label = savePreview.changedCount === 1 ? 'POI' : 'POIs';
      showSnackBar(`Successfully updated ${savePreview.changedCount} ${label}`, 'success');
      formik.resetForm();
      onSaved();
      onClose();
    } catch (error) {
      const serverMessage = (error as { graphQLErrors?: Array<{ message: string }> })
        ?.graphQLErrors?.[0]?.message;
      showSnackBar(serverMessage ?? 'Failed to update POIs. Please try again.', 'error');
    }
  }, [
    savePreview,
    structureId,
    templateId,
    bulkUpdatePointOfInterests,
    showSnackBar,
    formik,
    onSaved,
    onClose,
  ]);

  const hasInitializedRef = useRef(false);
  const resetFormRef = useRef(formik.resetForm);
  resetFormRef.current = formik.resetForm;

  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }
    if (loading || queryError || pois.length === 0) return;
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    resetFormRef.current({ values: initialValues });
  }, [open, loading, queryError, pois.length, initialValues]);

  const changedPoiCount = useMemo(
    () =>
      pois.filter((poi) => {
        const current = formik.values[poi.id];
        const initial = initialValues[poi.id];
        if (!current || !initial) return false;
        return Object.keys(current).some((key) => current[key] !== initial[key]);
      }).length,
    [pois, formik.values, initialValues]
  );

  const hasBulkEditableColumns = columns.some((col) => col.isBulkEditable);

  const handleCloseRequest = useCallback(() => {
    if (isSaving) return;
    if (changedPoiCount > 0) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  }, [isSaving, changedPoiCount, onClose]);

  const handleConfirmDiscard = useCallback(() => {
    setConfirmDiscardOpen(false);
    formik.resetForm();
    onClose();
  }, [formik, onClose]);

  const handleRetry = useCallback(() => {
    void refetchTemplate();
    void refetchPois();
  }, [refetchTemplate, refetchPois]);

  const handleVisibleColumnsChange = useCallback(
    (keys: string[]) => {
      const mandatorySet = new Set<string>(BULK_EDIT_MANDATORY_KEYS);
      const withMandatory = [
        ...BULK_EDIT_MANDATORY_KEYS,
        ...keys.filter((key) => !mandatorySet.has(key)),
      ];

      // Reset values for columns being hidden so they don't ghost-submit
      const currentVisible = new Set(effectiveVisibleColumnKeys);
      const newVisible = new Set(withMandatory);
      const hiddenKeys = [...currentVisible].filter((k) => !newVisible.has(k));

      if (hiddenKeys.length > 0) {
        const resetValues = { ...formik.values };
        for (const poiId of Object.keys(resetValues)) {
          const poiInitial = initialValues[poiId] ?? {};
          const updatedPoiValues = { ...resetValues[poiId] };
          for (const colKey of hiddenKeys) {
            updatedPoiValues[colKey] = poiInitial[colKey] ?? '';
          }
          resetValues[poiId] = updatedPoiValues;
        }
        formik.setValues(resetValues);
      }

      setVisibleColumnKeys(withMandatory);
      try {
        if (columnStorageKey) {
          localStorage.setItem(columnStorageKey, JSON.stringify(withMandatory));
        }
      } catch {
        // ignore storage quota errors
      }
    },
    [effectiveVisibleColumnKeys, formik, initialValues, columnStorageKey]
  );

  const poiLabel = selectedPoiIds.length === 1 ? 'POI' : 'POIs';

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseRequest}
        ModalProps={{ disableEnforceFocus: true }}
        PaperProps={{ sx: { width: '90vw' } }}
      >
        <Stack
          sx={{
            paddingTop: `${navbarHeight}px`,
            height: '100%',
            overflow: 'hidden',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderBottom: `1px solid ${abyssColors.primary[100]}`,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: '2.4rem', color: abyssColors.primary[500] }}
            >
              Editing {selectedPoiIds.length} {poiLabel}
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              {changedPoiCount > 0 && (
                <Typography sx={{ fontSize: '1.2rem', color: abyssColors.primary[300] }}>
                  {changedPoiCount} of {selectedPoiIds.length}{' '}
                  {changedPoiCount === 1 ? 'POI' : 'POIs'} modified
                </Typography>
              )}
              {changedPoiCount > 0 && !isSaving && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => formik.resetForm({ values: initialValues })}
                  sx={{
                    fontSize: '1.2rem',
                    color: abyssColors.primary[300],
                    textTransform: 'none',
                  }}
                >
                  Reset all
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={
                  isSaving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />
                }
                onClick={handleSaveRequest}
                disabled={isSaving || loading || Boolean(queryError) || !hasBulkEditableColumns}
                sx={{
                  backgroundColor: primary.darkBlue,
                  fontSize: '1.4rem',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
              <IconButton onClick={handleCloseRequest} size="small" disabled={isSaving}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 2 }}>
            {loading ? (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}
              >
                <CircularProgress />
              </Box>
            ) : queryError ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  gap: 2,
                }}
              >
                <ErrorOutline sx={{ fontSize: 48, color: 'error.main' }} />
                <Typography sx={{ fontSize: '1.4rem', color: abyssColors.primary[400] }}>
                  Failed to load data. Please try again.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleRetry}
                  sx={{ textTransform: 'none', fontSize: '1.4rem' }}
                >
                  Retry
                </Button>
              </Box>
            ) : !hasBulkEditableColumns ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  gap: 2,
                }}
              >
                <InfoOutlined sx={{ fontSize: 48, color: abyssColors.primary[200] }} />
                <Typography
                  sx={{ fontSize: '1.4rem', color: abyssColors.primary[400], fontWeight: 600 }}
                >
                  No fields configured for bulk editing
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1.2rem',
                    color: abyssColors.primary[300],
                    textAlign: 'center',
                    maxWidth: 360,
                  }}
                >
                  Contact an administrator to enable bulk editing on fields in this template.
                </Typography>
              </Box>
            ) : (
              <form
                onSubmit={formik.handleSubmit}
                style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
              >
                <BulkEditTable
                  pois={pois}
                  columns={columns}
                  values={formik.values}
                  errors={formik.errors as FormikErrors<BulkEditFormValues>}
                  setFieldValue={formik.setFieldValue}
                  setValues={formik.setValues}
                  visibleColumnKeys={effectiveVisibleColumnKeys}
                  onVisibleColumnsChange={handleVisibleColumnsChange}
                  initialValues={initialValues}
                />
              </form>
            )}
          </Box>
        </Stack>
      </Drawer>

      <ConfirmationDialog
        isOpen={confirmDiscardOpen}
        title="Discard changes?"
        description="You have unsaved changes. Closing will discard them permanently."
        confirmButtonText="Discard"
        cancelButtonText="Keep Editing"
        onConfirm={handleConfirmDiscard}
        handleClose={() => setConfirmDiscardOpen(false)}
      />

      <ConfirmationDialog
        isOpen={savePreview !== null}
        title="Save changes?"
        description={`You are about to update ${savePreview?.changedCount ?? 0} ${
          (savePreview?.changedCount ?? 0) === 1 ? 'POI' : 'POIs'
        }.`}
        confirmButtonText="Save"
        cancelButtonText="Cancel"
        onConfirm={() => void handleConfirmSave()}
        handleClose={() => setSavePreview(null)}
      >
        {savePreview !== null && savePreview.fields.length > 0 && (
          <Box sx={{ px: 2, pb: 1 }}>
            {savePreview.fields.map(({ label, count }) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.5,
                  borderBottom: `1px solid ${abyssColors.primary[100]}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ fontSize: '1.3rem', color: abyssColors.primary[500] }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: '1.3rem', color: abyssColors.primary[300] }}>
                  {count} {count === 1 ? 'POI' : 'POIs'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </ConfirmationDialog>
    </>
  );
};
